import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const DataHealthView = withListOverflow(DataHealthListOwner);
// src/features/data-health/DataHealthView.jsx
//
// Slice U6 — Data Health portfolio page. Faithful port of the legacy
// DataDashboard (HTML §14.5, lines 9555+), retargeted onto the U6
// server endpoints:
//
//   GET /api/integrations/health  → integrationHealthClient.listIntegrationHealth()
//   GET /api/sync-events          → integrationHealthClient.listSyncEvents()
//
// Layout (top to bottom):
//   1. Page header
//   2. SummaryBar — Overall + Healthy / Degraded / Down counts. When
//      every source is "pending" (no events anywhere), the overall
//      tile shows "All connections pending integration" instead of
//      0/0/0 — pending is a positive waiting-for-setup state, not a
//      failure (per slice spec).
//   3. ConnectionCards — 4 cards (Workday, Envoy, Verkada, Spacebase),
//      one per known integration. "Not yet connected" empty state for
//      pending sources.
//   4. SyncEventsTable — 200 most recent events by default, filterable
//      by source pill and "Failures Only".
//
// Note on the 4-card divergence from legacy: legacy combined Envoy +
// Verkada into a single card. The schema CHECK on sync_events.source_id
// keeps them separate; the spec said 4 cards; we follow that.

import React, { useEffect, useMemo, useState } from "react";
import {
  listIntegrationHealth,
  listSyncEvents,
} from "../../data/integrationHealthClient.js";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { KpiCard, KpiCardRow } from "../../components/KpiCard.jsx";
import { log } from "../../utils/logger.js";

// ─── Status colour map ────────────────────────────────────────────────────

const STATUS_COLOR = {
  green:   T.green,
  yellow:  T.amber,
  red:     T.red,
  pending: T.mid,
};
const STATUS_BG = {
  green:   T.utilGreen,
  yellow:  T.utilAmber,
  red:     T.utilRed,
  pending: T.lightGray,
};
const STATUS_LABEL = {
  green:   "Healthy",
  yellow:  "Degraded",
  red:     "Down",
  pending: "Pending integration",
};

// ─── Time formatters (lifted from legacy DataDashboard) ──────────────────

function fmtRelTime(isoStr) {
  if (!isoStr) return "—";
  const diffMs = Date.now() - new Date(isoStr).getTime();
  if (diffMs < 0) return "just now";
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(diffMs / 3_600_000);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(diffMs / 86_400_000)}d ago`;
}
function fmtNextSync(isoStr) {
  if (!isoStr) return "—";
  const diffMs = new Date(isoStr).getTime() - Date.now();
  if (diffMs <= 0) return "overdue";
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `in ${mins}m`;
  return `in ~${Math.floor(diffMs / 3_600_000)}h`;
}
function fmtLogTime(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
    ", " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
  );
}
function fmtDuration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ─── SummaryBar (KPI card row) ────────────────────────────────────────────

function SummaryBar({ connections }) {
  const greenCount   = connections.filter((c) => c.healthStatus === "green").length;
  const yellowCount  = connections.filter((c) => c.healthStatus === "yellow").length;
  const redCount     = connections.filter((c) => c.healthStatus === "red").length;
  const pendingCount = connections.filter((c) => c.healthStatus === "pending").length;

  const allPending = connections.length > 0 && pendingCount === connections.length;

  const overallStatus =
    allPending ? "pending"
    : redCount    > 0 ? "red"
    : yellowCount > 0 ? "yellow"
    : "green";
  const overallLabel =
    allPending ? "All connections pending integration"
    : overallStatus === "red"    ? "Critical"
    : overallStatus === "yellow" ? "Degraded"
    : "Healthy";

  return (
    <KpiCardRow testid="data-health-summary">
      <KpiCard
        testid="summary-overall"
        label="Overall System Health"
        value={overallLabel}
        valueColor={STATUS_COLOR[overallStatus]}
      />
      <KpiCard
        testid="summary-healthy"
        label="Healthy"
        value={greenCount}
        valueColor={T.green}
      />
      <KpiCard
        testid="summary-degraded"
        label="Degraded"
        value={yellowCount}
        valueColor={T.amber}
      />
      <KpiCard
        testid="summary-down"
        label="Down"
        value={redCount}
        valueColor={T.red}
      />
    </KpiCardRow>
  );
}

// ─── ConnectionCard ──────────────────────────────────────────────────────

function ConnectionCard({ conn, onViewHistory }) {
  const sc = STATUS_COLOR[conn.healthStatus];
  const sb = STATUS_BG[conn.healthStatus];
  const isPending = conn.healthStatus === "pending";
  const cadenceLabel = conn.cadenceHours >= 24
    ? `${conn.cadenceHours / 24}d`
    : `${conn.cadenceHours}h`;

  return (
    <div
      data-testid={`connection-card-${conn.sourceId}`}
      style={{
        background: T.card, borderRadius: 10, border: `1px solid ${T.border}`,
        padding: 18, display: "flex", flexDirection: "column", gap: 14,
        position: "relative", overflow: "hidden",
        opacity: isPending ? 0.85 : 1,
      }}
    >
      {/* Accent strip */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: sc }} />

      {/* Top row: name + status pill */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginTop: 4 }}>
        <div>
          <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>{conn.displayName}</div>
          <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>{conn.type}</div>
        </div>
        <div
          data-testid={`status-pill-${conn.sourceId}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "2px 10px", borderRadius: 6,
            background: sb, fontSize: T.fsXs, fontWeight: T.fwSemibold, color: sc, flexShrink: 0,
          }}
        >
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: sc }} />
          {STATUS_LABEL[conn.healthStatus]}
        </div>
      </div>

      {isPending ? (
        // ── Empty state: not yet connected ───────────────────────────
        <div
          data-testid={`pending-state-${conn.sourceId}`}
          style={{
            background: T.lightGray, borderRadius: 8, padding: "14px 14px 16px",
            border: `1px dashed ${T.border}`,
          }}
        >
          <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
            Not yet connected
          </div>
          <div style={{ fontSize: T.fsXs, color: T.mid, lineHeight: 1.5 }}>
            Manual data in use. {conn.displayName} sync will populate this card when integration ships.
          </div>
        </div>
      ) : (
        <>
          {/* ── Metrics grid ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { label: "Last Sync",        value: fmtRelTime(conn.lastSuccessAt) },
              { label: "Cadence",          value: cadenceLabel                   },
              { label: "Records",          value: conn.lastRecordCount?.toLocaleString() ?? "—" },
              { label: `Success (${conn.attemptsConsidered} syncs)`,
                                            value: `${Math.round((conn.successRate || 0) * conn.attemptsConsidered)} / ${conn.attemptsConsidered}` },
            ].map((m) => (
              <div key={m.label} style={{ background: T.lightGray, borderRadius: 6, padding: "7px 10px" }}>
                <div style={{ fontSize: T.fsXxs, color: T.mid, marginBottom: 2 }}>{m.label}</div>
                <div style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark }}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* ── Health score bar ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: T.fsXxs, color: T.mid, width: 72, flexShrink: 0 }}>Health Score</span>
            <div style={{ flex: 1, height: 5, background: T.border, borderRadius: 99, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${conn.healthScore}%`, background: sc, borderRadius: 99 }} />
            </div>
            <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: sc, width: 28, textAlign: "right", flexShrink: 0 }}>
              {conn.healthScore}
            </span>
          </div>

          {/* ── Footer: next sync + view history ── */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            borderTop: `1px solid ${T.border}`, paddingTop: 10, marginTop: -4,
          }}>
            <span style={{ fontSize: T.fsXs, color: T.mid }}>Next: {fmtNextSync(conn.nextSyncDue)}</span>
            <button
              data-testid={`view-history-${conn.sourceId}`}
              onClick={() => onViewHistory(conn.sourceId)}
              style={{
                fontSize: T.fsXs, color: T.blue, background: "none", border: "none",
                cursor: "pointer", padding: 0, textDecoration: "underline", fontFamily: "inherit",
              }}
            >
              View History
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── DataHealthTabPane ───────────────────────────────────────────────────
//
// Embeddable pane for the Admin → Data → Health tab.
// Contains the fetch logic + SummaryBar + ConnectionCards only.
// No SyncEventsTable, no IngestionHistoryPanel.

export function DataHealthTabPane() {
  const [conns,     setConns]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([listIntegrationHealth(), listSyncEvents()])
      .then(([connRows]) => {
        if (cancelled) return;
        setConns(connRows);
        log.api("DataHealthTabPane loaded", { connections: connRows.length, reloadKey });
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("DataHealthTabPane load failed", { name: err?.name, msg: err?.message });
        setError(err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const handleRefresh = () => setReloadKey((n) => n + 1);

  return (
    <div data-testid="data-health-tab-pane" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      {loading ? (
        <div
          data-testid="data-health-loading"
          style={{ padding: 40, textAlign: "center", color: T.mid, fontSize: T.fsBase }}
        >
          Loading integration health…
        </div>
      ) : (
        <>
          <SummaryBar connections={conns} />
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, textTransform: "uppercase", letterSpacing: "0.07em", color: T.mid }}>
                Connections ({conns.length})
              </div>
              <button
                type="button"
                data-testid="data-health-refresh"
                onClick={handleRefresh}
                style={{
                  fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.blue, background: "none",
                  border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 12px",
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                ↻ Refresh
              </button>
            </div>
            <div
              data-testid="connection-cards-grid"
              style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}
            >
              {conns.map((conn) => (
                <ConnectionCard key={conn.sourceId} conn={conn} onViewHistory={() => {}} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Top-level standalone page (kept for the __e2e=data-health dev fixture) ──

function DataHealthListOwner() {
  return (
    <div>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 0 48px" }}>
        <DataHealthTabPane />
      </div>
    </div>
  );
}

// ─── Retired: SyncEventsTable (kept for reference only — no longer rendered) ─

const SOURCE_FILTERS = [
  { key: "all",       label: "All Sources" },
  { key: "workday",   label: "Workday"     },
  { key: "envoy",     label: "Envoy"       },
  { key: "verkada",   label: "Verkada"     },
  { key: "spacebase", label: "Spacebase"   },
  { key: "failures",  label: "Failures Only" },
];

function SyncEventsTable({ events, filter, setFilter }) {
  const filtered = useMemo(() => {
    if (filter === "all")      return events;
    if (filter === "failures") return events.filter((e) => !!e.errorCode);
    return events.filter((e) => e.sourceId === filter);
  }, [events, filter]);

  return (
    <div
      data-testid="sync-events-section"
      style={{ background: T.card, borderRadius: 10, border: `1px solid ${T.border}`, padding: 20 }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <span style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>Sync Event Log</span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {SOURCE_FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`event-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              style={{
                padding: "4px 11px", borderRadius: 20, fontSize: T.fsXs, cursor: "pointer", fontFamily: "inherit",
                border: `1px solid ${filter === f.key ? T.blue : T.border}`,
                background: filter === f.key ? T.blueSoft : T.card,
                color: filter === f.key ? T.blue : T.mid,
                fontWeight: filter === f.key ? 700 : 500,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div
          data-testid="sync-events-empty"
          style={{ padding: 32, textAlign: "center", color: T.mid, fontSize: T.fsBase }}
        >
          {events.length === 0
            ? "No sync events recorded yet."
            : "No sync events for the selected filter."}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table data-testid="sync-events-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}>
            <thead>
              <tr style={{ background: T.hoverBg, borderBottom: `2px solid ${T.border}` }}>
                {["Source", "Type", "Started", "Duration", "Records", "Status", "Error"].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: 0.5, color: T.mid, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => {
                const isFail = !!e.errorCode;
                const statusKey = e.healthStatusAfter || (isFail ? "red" : "green");
                const sc = STATUS_COLOR[statusKey] || T.mid;
                const sb = STATUS_BG[statusKey]    || T.lightGray;
                const recsAcc = e.recordsAccepted ?? null;
                const recsRej = e.recordsRejected ?? null;
                const recsLabel = recsAcc == null && recsRej == null
                  ? "—"
                  : `${recsAcc ?? 0}${recsRej ? ` (-${recsRej})` : ""}`;
                return (
                  <tr key={e.eventId} style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${T.border}` : "none" }}>
                    <td style={{ padding: "10px 12px", fontWeight: T.fwSemibold, color: T.dark, whiteSpace: "nowrap" }}>{e.sourceId}</td>
                    <td style={{ padding: "10px 12px", color: T.mid, whiteSpace: "nowrap" }}>{e.eventType}</td>
                    <td style={{ padding: "10px 12px", color: T.mid, whiteSpace: "nowrap" }}>{fmtLogTime(e.startedAt)}</td>
                    <td style={{ padding: "10px 12px", color: T.mid, whiteSpace: "nowrap" }}>{fmtDuration(e.durationMs)}</td>
                    <td style={{ padding: "10px 12px", color: T.dark, whiteSpace: "nowrap" }}>{recsLabel}</td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: sc, background: sb, padding: "2px 8px", borderRadius: 10 }}>
                        {STATUS_LABEL[statusKey] || statusKey}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px", color: isFail ? T.red : T.mid, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis" }}
                        title={e.errorMessage || ""}>
                      {e.errorMessage || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

